import React, { useState } from "react";
import { submitRegistrationRequest } from "../api";
import { LoadingOverlay } from "../components/LoadingOverlay";

const NAVY = "#1e3a8a";
const TEAL = "#00c9a7";

// billing-module-enhancement AC19: for B2B prospects whose purchasing process can't
// produce a company card on the spot. Submits to the existing (already-built)
// POST /api/auth/register-request — super admin reviews and approves manually,
// provisioning a no-card trial the same way super-admin-created tenants already work.
export default function RequestAccessPage() {
  const [companyName,  setCompanyName]  = useState("");
  const [domain,       setDomain]       = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [message,      setMessage]      = useState("");
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState("");
  const [submitted,    setSubmitted]    = useState(false);

  const canSubmit = companyName.trim() && domain.trim() && contactEmail.trim() && !loading;

  async function handleSubmit() {
    setLoading(true);
    setError("");
    try {
      await submitRegistrationRequest({
        company_name:  companyName.trim(),
        domain:        domain.replace(/^@/, "").trim(),
        contact_email: contactEmail.trim(),
        message:       message.trim() || undefined,
      });
      setSubmitted(true);
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "11px 14px", borderRadius: 10, fontSize: 14,
    border: "1.5px solid #e2e8f0", outline: "none", boxSizing: "border-box",
    color: "#1e293b", marginBottom: 12, fontFamily: "inherit",
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: `linear-gradient(135deg, ${NAVY} 0%, #0f172a 100%)`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "var(--font-sans)", padding: "32px 16px",
    }}>
      <LoadingOverlay active={loading} />
      <div style={{
        background: "white", borderRadius: 16, padding: "40px 36px",
        width: "min(460px, calc(100vw - 32px))",
        boxShadow: "0 25px 50px rgba(0,0,0,0.3)",
      }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <img src="/favicon.png" alt="HRxAI" style={{
            width: 56, height: 56, borderRadius: 12, objectFit: "contain",
            margin: "0 auto 14px", display: "block",
          }} />
          <h1 style={{ color: NAVY, fontSize: 22, fontWeight: 800, margin: "0 0 2px" }}>
            HRx<span style={{ color: TEAL }}>AI</span>
          </h1>
          <p style={{ color: "#64748b", fontSize: 13, margin: 0 }}>Request access — no card needed</p>
        </div>

        {submitted ? (
          <div style={{ textAlign: "center", padding: "12px 0" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <p style={{ color: "#1e293b", fontSize: 15, fontWeight: 700, marginBottom: 8 }}>
              Request received
            </p>
            <p style={{ color: "#64748b", fontSize: 13, lineHeight: 1.6 }}>
              We'll review your request and email a setup link to <b>{contactEmail}</b> once
              approved — usually within one business day.
            </p>
            <a href="/pricing" style={{ color: TEAL, fontWeight: 600, fontSize: 13, textDecoration: "none", display: "inline-block", marginTop: 16 }}>
              ← Back to pricing
            </a>
          </div>
        ) : (
          <>
            <p style={{ color: "#64748b", fontSize: 13, lineHeight: 1.6, marginBottom: 20, textAlign: "center" }}>
              If your company's purchasing process can't produce a card right now, tell us a
              bit about your team and we'll set up a 14-day trial for you — no card required.
            </p>

            {error && (
              <div style={{
                background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8,
                padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#dc2626",
              }}>{error}</div>
            )}

            <input
              type="text" placeholder="Company name" value={companyName}
              onChange={e => setCompanyName(e.target.value)} style={inputStyle}
            />
            <input
              type="text" placeholder="Company domain (e.g. acme.com)" value={domain}
              onChange={e => setDomain(e.target.value)} style={inputStyle}
            />
            <input
              type="email" placeholder="Work email" value={contactEmail}
              onChange={e => setContactEmail(e.target.value)} style={inputStyle}
            />
            <textarea
              placeholder="Anything we should know? (optional)" value={message}
              onChange={e => setMessage(e.target.value)} rows={3}
              style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }}
            />

            <button
              style={{
                width: "100%", padding: "13px 0", borderRadius: 10, border: "none",
                background: canSubmit ? `linear-gradient(135deg, ${NAVY}, ${TEAL})` : "#94a3b8",
                color: "white", fontSize: 15, fontWeight: 700,
                cursor: canSubmit ? "pointer" : "not-allowed",
                marginTop: 4,
              }}
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              {loading ? "Sending…" : "Request access →"}
            </button>

            <p style={{ color: "#64748b", fontSize: 13, marginTop: 16, textAlign: "center" }}>
              Have a company card?{" "}
              <a href="/signup" style={{ color: TEAL, fontWeight: 600, textDecoration: "none" }}>Start a self-serve trial instead</a>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
